import React from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { 
  ArrowLeft, Download, Loader2, Briefcase, Mail, Phone, MapPin,
  CheckCircle, Send, Sparkles, Clock
} from 'lucide-react';
import { toast } from 'sonner';
import { getStatusBadgeClass, getStatusLabel, formatDate } from '../../../lib/utils';
import { api } from '../../../lib/api';
import QualificationResultCard from '../QualificationResultCard';

const ProfileHeader = ({ 
  candidate, candidateId, expBadge, qualification,
  sendEvaluationForm, sendingEval, downloadCV, fetchCandidate
}) => {
  const handleCategoryChange = async (e) => {
    const newCat = e.target.value;
    try {
      const res = await api.patch(`/admin/candidates/${candidateId}/profile`, { primary_category: newCat });
      if (res.ok) {
        toast.success('Type de poste mis à jour');
        fetchCandidate();
      }
    } catch (err) { toast.error('Erreur'); }
  };

  const handleAIEnrich = async () => {
    toast.info("Enrichissement IA en cours...");
    try {
      const res = await api.post(`/admin/candidates/${candidateId}/enrich`);
      if (res.ok) {
        toast.success("Profil enrichi par l'IA");
        fetchCandidate();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Échec enrichissement");
      }
    } catch (err) { toast.error("Erreur de connexion"); }
  };

  return (
    <div className="mb-6">
      <Link to="/admin/candidates" className="inline-flex items-center text-sm text-tp-text-light hover:text-tp-primary mb-4">
        <ArrowLeft className="h-4 w-4 mr-1" />
        Retour aux candidats
      </Link>
      
      <div className="space-y-4">
        {/* Avatar + Name */}
        <div className="flex items-start gap-3 md:gap-4">
          <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-tp-primary flex items-center justify-center text-white text-lg md:text-xl font-display font-semibold flex-shrink-0">
            {(candidate.first_name || 'U')[0]}{(candidate.last_name || 'N')[0]}
          </div>
          
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <h1 className="text-xl md:text-2xl font-display font-semibold text-tp-text">
                {candidate.first_name || 'Inconnu'} {candidate.last_name || ''}
              </h1>
              <Badge className={expBadge.class}>{expBadge.label}</Badge>
            </div>
            
            {/* Type de poste */}
            <div className="flex items-center gap-2 mb-2">
              <Briefcase className="h-4 w-4 text-tp-accent flex-shrink-0" />
              <select
                value={candidate.primary_category || candidate.job_category || ''}
                onChange={handleCategoryChange}
                className="font-semibold text-tp-primary text-base md:text-lg bg-transparent border-0 border-b border-dashed border-tp-primary/30 focus:border-tp-primary focus:outline-none cursor-pointer appearance-none pr-6"
                style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%230F2744\' stroke-width=\'2\'%3E%3Cpath d=\'M6 9l6 6 6-6\'/%3E%3C/svg%3E")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right 0 center' }}
                data-testid="candidate-job-type-select"
              >
                <optgroup label="Marketing & Acquisition">
                  <option value="community_manager">Community Manager</option>
                  <option value="media_buyer">Media Buyer (Ads)</option>
                  <option value="seo">SEO / Référencement</option>
                  <option value="email_marketing">Email Marketing / CRM</option>
                  <option value="lead_generation">Lead Generation / Setter</option>
                </optgroup>
                <optgroup label="Vente">
                  <option value="commercial_b2b">Commercial B2B</option>
                  <option value="closer">Closer (high-ticket)</option>
                </optgroup>
                <optgroup label="Contenu">
                  <option value="graphiste">Graphiste / Designer</option>
                  <option value="ux_ui_designer">UX/UI Designer</option>
                  <option value="monteur_video">Monteur Vidéo</option>
                  <option value="copywriter">Copywriter / Rédacteur</option>
                </optgroup>
                <optgroup label="Tech & Automation">
                  <option value="developpeur_web">Développeur Web</option>
                  <option value="ai_automation">AI Automation Specialist</option>
                  <option value="no_code_builder">No-code Builder</option>
                  <option value="funnel_builder">Funnel Builder</option>
                </optgroup>
                <optgroup label="Support & Ops">
                  <option value="assistant_virtuel">Assistant(e) Virtuel(le)</option>
                  <option value="support_client">Support Client</option>
                  <option value="comptabilite">Comptabilité / Gestion</option>
                  <option value="recruteur_rh">Recruteur / RH</option>
                  <option value="chef_de_projet">Chef de projet</option>
                </optgroup>
                <optgroup label="Autre">
                  <option value="autre">Autre</option>
                </optgroup>
              </select>
              {/* Free-text label when candidate picked "Autre" — gives admin context to re-classify */}
              {candidate.primary_category === 'autre' && candidate.other_category_label && (
                <span
                  className="text-xs italic text-orange-700 bg-orange-50 border border-orange-200 rounded px-2 py-0.5 ml-1"
                  title="Métier précisé par le candidat (catégorie Autre)"
                  data-testid="candidate-other-category-label"
                >
                  → {candidate.other_category_label}
                </span>
              )}
            </div>
            
            {/* Contact info */}
            <div className="flex flex-col sm:flex-row sm:flex-wrap gap-1 sm:gap-3 text-sm text-tp-text-light">
              <span className="flex items-center gap-1 truncate">
                <Mail className="h-3.5 w-3.5 flex-shrink-0" /><span className="truncate">{candidate.email}</span>
              </span>
              <span className="flex items-center gap-1">
                <Phone className="h-3.5 w-3.5 flex-shrink-0" />{candidate.phone}
              </span>
              <span className="flex items-center gap-1">
                <MapPin className="h-3.5 w-3.5 flex-shrink-0" />{candidate.city}, {candidate.country}
              </span>
            </div>
          </div>
        </div>
        
        {/* Badges & Actions */}
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <Badge className={getStatusBadgeClass(candidate.status)}>{getStatusLabel(candidate.status)}</Badge>
            {qualification ? (
              <QualificationResultCard qualification={qualification} compact={true} />
            ) : (
              <Badge variant="outline" className="text-tp-text-light border-tp-border">Non qualifié</Badge>
            )}
          </div>
          
          <div className="flex flex-wrap items-center gap-1.5">
            {!qualification && (
              <Button size="sm" onClick={sendEvaluationForm} disabled={sendingEval} className="bg-tp-accent hover:bg-tp-accent-light text-white text-xs h-8 px-2.5" data-testid="send-evaluation-btn">
                {sendingEval ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                <span className="ml-1">Évaluation</span>
              </Button>
            )}
            {qualification && ['A_RECRUTER', 'A_TESTER'].includes(qualification.decision) && (
              <span className="text-xs text-tp-text-light italic px-2 py-1">
                Entretien à planifier directement par email ou téléphone.
              </span>
            )}
            {candidate.cv_storage_path && (
              <Button variant="outline" size="sm" onClick={downloadCV} className="border-tp-border text-xs h-8 px-2.5" data-testid="download-cv-button">
                <Download className="h-3.5 w-3.5" /><span className="ml-1">CV</span>
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={handleAIEnrich} className="border-tp-primary/30 text-tp-primary hover:bg-tp-primary/10 text-xs h-8 px-2.5" data-testid="ai-enrich-btn">
              <Sparkles className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
        
        {/* Status indicators */}
        {candidate.evaluation_sent && !qualification && (
          <div className="flex items-center gap-2 text-sm text-tp-primary bg-tp-primary/5 px-3 py-2 rounded-lg border border-tp-primary/20">
            <Clock className="h-4 w-4" />
            <span>Formulaire envoyé le {formatDate(candidate.evaluation_sent_at)} — En attente de réponse</span>
          </div>
        )}
        {qualification && (
          <div className="flex items-center gap-2 text-sm text-tp-accent bg-tp-accent/10 px-3 py-2 rounded-lg border border-tp-accent/20">
            <CheckCircle className="h-4 w-4" />
            <span>Formulaire rempli — Score {qualification.score_total}/100 — {qualification.decision_label}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfileHeader;
